/**
 * Every route of every stream, in one list: Routes (the menu item that used
 * to open Destinations, under the same `destinations` nav tab).
 *
 * An overview and a way in, not an editor. A route is one tier's link to one
 * destination; its price, caps and filters are edited on its stream's
 * routing, and the destination's address on its own page, exactly as before.
 * Prices and caps are what routing uses, resolved by the server with the
 * engine's own rules (`GET /api/v1/routes`).
 *
 * Kinds: sell (to a data buyer), assign (to a rep, through a team's
 * rotation) and send (to a webhook with no buyer). Money only shows on the
 * routes that can earn it.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { IconSearch, IconSend } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useRoutesOverview } from '../../api/queries';
import { useTeams } from '../../api/teams';
import { useAuth } from '../../auth/AuthProvider';
import { MonoSegmented } from '../../ui';
import { useVocab } from '../../vocab';
import { DaysBadge } from '../ActiveDays';
import { CapCell, PriceCell, type Price } from '../CampaignDeliveryTab';
import { useCrmPermissions, useOwnerOptions } from '../crm/crmShared';
import { KIND_COLOUR, kindLabel } from './streamShared';

type Kind = 'all' | 'sell' | 'assign' | 'send';

export function RoutesPage() {
  const v = useVocab();
  const navigate = useNavigate();
  const { data, isPending, error } = useRoutesOverview();
  const { user } = useAuth();
  const perms = useCrmPermissions();
  // Reps and teams by name, for assignment routes. Only fetched when there
  // is one to name.
  const hasAssign = (data?.routes ?? []).some((r) => r.kind === 'assign');
  const reps = new Map(useOwnerOptions(hasAssign && perms.seesPeople).map((o) => [o.value, o.label]));
  const teams = new Map(
    (useTeams(user?.tenant_id, hasAssign).data?.teams ?? []).map((t) => [t.id, t.name]),
  );
  const [kind, setKind] = useState<Kind>('all');
  const [q, setQ] = useState('');

  const routes = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.routes ?? []).filter((r) => {
      if (kind !== 'all' && r.kind !== kind) return false;
      if (!needle) return true;
      return [r.destination_name, r.campaign_name, r.group_name, r.buyer_name]
        .some((x) => x.toLowerCase().includes(needle));
    });
  }, [data, kind, q]);
  const counts = useMemo(() => {
    const c = { sell: 0, assign: 0, send: 0 };
    for (const r of data?.routes ?? []) if (r.kind in c) c[r.kind as keyof typeof c] += 1;
    return c;
  }, [data]);
  const unlinked = data?.unlinked ?? [];

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>{v.Many('route')}</Title>
          <Text size="sm" c="dimmed" maw={680}>
            {`Where each ${v.one('campaign')}'s ${v.many('lead')} go: sold to ${v.a('buyer')}, assigned to a rep, or sent to a webhook. Prices and caps are the ones routing uses. Change them on the ${v.one('campaign')}'s routing.`}
          </Text>
        </div>
        <Button variant="default" leftSection={<IconSend size={16} />} component={Link} to="/destinations">
          {`Manage ${v.many('destination')}`}
        </Button>
      </Group>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}

      {unlinked.length > 0 && (
        <Alert color="chartAmber" variant="light" title="Set up but never used">
          <Text size="sm">
            {`${unlinked.length} ${v.word(unlinked.length, 'destination')} ${unlinked.length === 1 ? 'is' : 'are'} in no ${v.one('campaign')}'s routing, so nothing is ever sent there: ${unlinked.map((d) => d.name || d.id).join(', ')}.`}
          </Text>
        </Alert>
      )}

      <Group gap="sm" wrap="wrap">
        <MonoSegmented
          value={kind}
          aria-label="Kind"
          onChange={(x) => setKind(x as Kind)}
          data={[
            { label: `All ${data?.routes.length ?? 0}`, value: 'all' },
            { label: `Sell ${counts.sell}`, value: 'sell' },
            { label: `Assign ${counts.assign}`, value: 'assign' },
            { label: `Send ${counts.send}`, value: 'send' },
          ]}
        />
        <TextInput
          size="xs"
          leftSection={<IconSearch size={14} />}
          placeholder="Search"
          aria-label={`Search ${v.many('route')}`}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={240}
        />
      </Group>

      <Card p={0}>
        {isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <Table highlightOnHover data-testid="routes-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{v.One('route')}</Table.Th>
                  <Table.Th>Kind</Table.Th>
                  <Table.Th>{v.One('campaign')}</Table.Th>
                  <Table.Th>Goes to</Table.Th>
                  <Table.Th ta="right">Price</Table.Th>
                  <Table.Th pl={28}>Daily cap</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {routes.length === 0 && (
                  <Table.Tr><Table.Td colSpan={7}>
                    <Text ta="center" c="dimmed" py="lg">
                      {(data?.routes.length ?? 0) === 0
                        ? `No ${v.many('route')} yet. Open ${v.a('campaign')} and add a tier to send its ${v.many('lead')} somewhere.`
                        : `No ${v.many('route')} match.`}
                    </Text>
                  </Table.Td></Table.Tr>
                )}
                {routes.map((r) => {
                  const rep = r.user_id ? reps.get(r.user_id) ?? 'A rep' : null;
                  const team = r.team_id ? teams.get(r.team_id) : null;
                  const status = !r.campaign_active
                    ? { label: `${v.One('campaign')} off`, colour: 'gray' }
                    : r.live
                      ? { label: 'Live', colour: 'chartGreen' }
                      : { label: 'Paused', colour: 'gray' };
                  const moneyHere = r.kind === 'sell' || (r.kind === 'send' && r.price.resolved > 0);
                  return (
                    <Table.Tr
                      key={r.link_id}
                      onClick={() => navigate(`/campaigns/${r.campaign_id}`)}
                      style={{ cursor: 'pointer' }}
                      opacity={r.live && r.campaign_active ? 1 : 0.6}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>{r.destination_name || '—'}</Text>
                        <Text size="xs" c="dimmed">
                          {r.kind === 'assign' ? `Rotation: ${r.group_name}` : `${v.One('routeGroup')} ${r.group_priority}: ${r.group_name}`}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={KIND_COLOUR[r.kind] ?? 'gray'}>{kindLabel(r.kind)}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Anchor
                          component={Link}
                          to={`/campaigns/${r.campaign_id}`}
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.campaign_name}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        {r.kind === 'sell' && (
                          r.company_id
                            ? (
                              <Anchor
                                component={Link}
                                to={`/companies/${r.company_id}?tab=buys`}
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {r.buyer_name || r.buyer_id}
                              </Anchor>
                            )
                            : <Text size="sm">{r.buyer_name || r.buyer_id}</Text>
                        )}
                        {r.kind === 'assign' && (
                          <Text size="sm">{team ? `${rep} (${team})` : rep}</Text>
                        )}
                        {r.kind === 'send' && (
                          <Tooltip label={`No ${v.one('buyer')}: whatever this address answers, nobody pays for it`}>
                            <Text size="sm" c="dimmed">A webhook</Text>
                          </Tooltip>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {moneyHere ? <PriceCell price={r.price as Price} /> : <Text size="sm" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td pl={28}>
                        <CapCell used={r.effective_caps.daily_count} cap={r.effective_caps.daily_cap} />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Badge variant="light" color={status.colour}>{status.label}</Badge>
                          <DaysBadge days={r.days} />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  );
}
